import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Quote } from "lucide-react";
import { useT, LangToggle } from "../i18n/LanguageContext";

const LOGO = "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/3m4xmkko_mother%20hippo%20PNG.png";
const STOP_FGM = "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/k1wg42ea_IMG-20260408-WA0020.jpg";

const copy = {
  en: {
    back: "Selector",
    nav: { mission: "Mission", programs: "Programs", stories: "Stories", donate: "Fund a Future" },
    issue: "Issue N° 01 · Winter 2026",
    heroA: "Reclaiming",
    heroAccent: "Our",
    heroB: "Narrative.",
    heroSub: "A world where every girl exercises full autonomy over her body. Join the Mother Hippo Foundation in eradicating Female Genital Mutilation through education, solidarity, and unflinching love.",
    readStory: "Read the Story",
    becomePatron: "Become a patron",
    pullquote: "If we don't speak up, who will?",
    founder: "— Sélifa Boukari McGreevy, Founder",
    stats: [
      { k: "200M+", v: "Women & girls alive today have been cut" },
      { k: "11s", v: "How often a girl is cut, somewhere in the world" },
      { k: "3M", v: "Girls at risk of FGM every year" },
      { k: "94.7%", v: "Of Togolese women want this to end" },
    ],
    missionKicker: "The Mission",
    missionHeading: "The power of",
    missionHeadingItalic: "her",
    missionHeadingEnd: "voice.",
    missionP1: "I was born in Sokodé, in central Togo. As a survivor of FGM, I founded Mother Hippo Foundation in 2018 because I refuse to let another girl walk the road I walked. The name reflects my philosophy of helping others — mothers help and protect us all. The Hippo represents not only Togo but a sacred symbol of Africa: hippo wisdom includes healing, protection of family, and the birth of new ideas.",
    missionP2: "FGM is not a religious, traditional, or medical obligation — it is a human rights violation. And it has no place in any society. So… HEAR US ROAR. Mother Hippo Foundation speaks up for every girl who has suffered, and works to eradicate this brutal, heinous practice and SAVE Our Daughters.",
    founderName: "Sélifa Boukari McGreevy",
    founderTitle: "Founder & Chairwoman · FGM Survivor",
    programsKicker: "Features",
    programsHeading: "Pathways to",
    programsHeadingAccent: "freedom",
    programsSub: "Three interlocking programs. One goal — a generation of girls who grow up whole, in every sense of the word.",
    programs: [
      { num: "I.", title: "FGM Advocacy Tour", excerpt: "A 22-day intensive tour through 12 Togolese cities — leadership training, public meetings, and survivor-led workshops in the regions where FGM prevalence is highest." },
      { num: "II.", title: "Voices Across Borders", excerpt: "An influencer campaign across the 18 African countries where FGM is most prevalent, culminating in a coordinated march on 6 February — International Day of Zero Tolerance for FGM." },
      { num: "III.", title: "Healing & Reconstruction", excerpt: "A planned partnership with doctors and hospitals to provide free clitoral reconstruction surgery, counselling, and rehabilitation for survivors." },
    ],
    readFeature: "Read the feature",
    storiesKicker: "Survivor Portraits",
    storiesHeading: "Voices that changed",
    storiesHeadingItalic: "everything",
    stories: [
      { name: "Julia Lalla-Maharajh", place: "CEO, The Orchid Project", quote: "It is a social norm that's deeply rooted within a community's identity system. It's almost like an unwritten moral code. It is held in place by taboo and silence." },
      { name: "Shelby Quast", place: "Americas Director, Equality Now", quote: "There has to be a huge shift so that we identify this as a form of violence against girls — and not something that's protected as cultural tradition." },
    ],
    donateKicker: "Patronage",
    donateHeading: "Fund a",
    donateHeadingAccent: "future",
    donateHeadingEnd: "— not a reaction.",
    donateSub: "Recurring patrons underwrite the long, patient work: one Circle, one scholarship, one safe year at a time.",
    tiers: ["$25 / mo", "$60 / mo", "$120 / mo", "Custom"],
    donateFoot: "100% of gifts fund programs · 501(c)(3) pending",
    contactKicker: "Correspondence",
    contactHeading: "Write us a",
    contactHeadingItalic: "letter.",
    contactSub: "Press, partnerships, survivor support, or an idea you can't let go of — we read every message personally.",
    labels: { email: "Email", press: "Phone", office: "Office" },
    email: "info@motherhippo.org",
    pressEmail: "+1 (725) 500-6839",
    offices: "Las Vegas, Nevada · Sokodé, Togo",
    footerTag: "A movement for dignity, protection, and lasting change.",
    rights: "© 2026 Mother Hippo Foundation",
  },
  fr: {
    back: "Sélecteur",
    nav: { mission: "Mission", programs: "Programmes", stories: "Récits", donate: "Financer l'Avenir" },
    issue: "Numéro N° 01 · Hiver 2026",
    heroA: "Réécrire",
    heroAccent: "Notre",
    heroB: "Récit.",
    heroSub: "Un monde où chaque fille exerce une autonomie totale sur son corps. Rejoignez la Mother Hippo Foundation pour éradiquer les Mutilations Génitales Féminines par l'éducation, la solidarité et un amour inébranlable.",
    readStory: "Lire l'histoire",
    becomePatron: "Devenir mécène",
    pullquote: "Si nous ne parlons pas, qui le fera ?",
    founder: "— Sélifa Boukari McGreevy, Fondatrice",
    stats: [
      { k: "200M+", v: "Femmes & filles excisées aujourd'hui" },
      { k: "11s", v: "Une fille excisée toutes les 11 secondes" },
      { k: "3M", v: "Filles à risque chaque année" },
      { k: "94,7%", v: "Des Togolaises veulent que cela cesse" },
    ],
    missionKicker: "La Mission",
    missionHeading: "Le pouvoir de",
    missionHeadingItalic: "sa",
    missionHeadingEnd: "voix.",
    missionP1: "Je suis née à Sokodé, au centre du Togo. Survivante des MGF, j'ai fondé la Mother Hippo Foundation en 2018 parce que je refuse qu'une autre fille emprunte le chemin que j'ai parcouru. Le nom reflète ma philosophie : les mères nous aident et nous protègent toutes. L'hippopotame représente non seulement le Togo mais un symbole sacré de l'Afrique — sa sagesse inclut la guérison, la protection de la famille et la naissance d'idées nouvelles.",
    missionP2: "Les MGF ne sont ni une obligation religieuse, ni traditionnelle, ni médicale — c'est une violation des droits humains. Et cela n'a sa place dans aucune société. Alors… ENTENDEZ-NOUS RUGIR. La Mother Hippo Foundation parle pour chaque fille qui a souffert, et œuvre à éradiquer cette pratique brutale pour SAUVER nos filles.",
    founderName: "Sélifa Boukari McGreevy",
    founderTitle: "Fondatrice & Présidente · Survivante des MGF",
    programsKicker: "Reportages",
    programsHeading: "Chemins vers la",
    programsHeadingAccent: "liberté",
    programsSub: "Trois programmes complémentaires. Un objectif — une génération de filles qui grandissent entières, dans tous les sens du mot.",
    programs: [
      { num: "I.", title: "Tournée de Plaidoyer MGF", excerpt: "Une tournée intensive de 22 jours à travers 12 villes togolaises — formation au leadership, réunions publiques et ateliers menés par des survivantes dans les régions où la prévalence des MGF est la plus élevée." },
      { num: "II.", title: "Voix Sans Frontières", excerpt: "Une campagne d'influenceuses dans les 18 pays africains où les MGF sont les plus répandues, culminant en une marche coordonnée le 6 février — Journée internationale de tolérance zéro à l'égard des MGF." },
      { num: "III.", title: "Soins & Reconstruction", excerpt: "Un partenariat prévu avec médecins et hôpitaux pour offrir gratuitement chirurgie reconstructive, accompagnement psychologique et réhabilitation aux survivantes." },
    ],
    readFeature: "Lire le reportage",
    storiesKicker: "Portraits de Survivantes",
    storiesHeading: "Des voix qui ont tout",
    storiesHeadingItalic: "changé",
    stories: [
      { name: "Aminata, 24", place: "Bamako, Mali", quote: "La Fondation ne m'a pas sauvée — elle m'a tendu la plume. C'est moi qui écris la suite pour ma fille." },
      { name: "Esi, 19", place: "Kumasi, Ghana", quote: "Je croyais le silence normal. Le Cercle m'a montré que mes grand-mères posaient les mêmes questions en secret." },
    ],
    donateKicker: "Mécénat",
    donateHeading: "Financer un",
    donateHeadingAccent: "avenir",
    donateHeadingEnd: "— pas une réaction.",
    donateSub: "Les mécènes récurrents soutiennent le travail long et patient : un Cercle, une bourse, une année en sécurité à la fois.",
    tiers: ["25 $ / mois", "60 $ / mois", "120 $ / mois", "Personnalisé"],
    donateFoot: "100% des dons financent les programmes · 501(c)(3) en cours",
    contactKicker: "Correspondance",
    contactHeading: "Écrivez-nous une",
    contactHeadingItalic: "lettre.",
    contactSub: "Presse, partenariats, soutien aux survivantes, ou une idée que vous n'arrivez pas à lâcher — nous lisons chaque message personnellement.",
    labels: { email: "Courriel", press: "Téléphone", office: "Bureaux" },
    email: "info@motherhippo.org",
    pressEmail: "+1 (725) 500-6839",
    offices: "Las Vegas, Nevada · Sokodé, Togo",
    footerTag: "Un mouvement pour la dignité, la protection et un changement durable.",
    rights: "© 2026 Mother Hippo Foundation",
  },
};

const programImgs = [
  "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/1avalk56_Screenshot%202026-04-30%20at%201.12.32%E2%80%AFPM.png",
  "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/4b9z0d2l_Screenshot%202026-04-30%20at%201.12.43%E2%80%AFPM.png",
  "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/cn9nk1cb_Screenshot%202026-04-30%20at%201.12.37%E2%80%AFPM.png",
];

export default function PrototypeEditorial() {
  const t = useT(copy);

  return (
    <div
      data-testid="prototype-editorial"
      className="min-h-screen bg-[#FFF8EC] text-[#1A1A1A] font-manrope selection:bg-[#00A651] selection:text-white"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FFF8EC]/85 border-b-2 border-[#00A651]/20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-black/60 hover:text-[#E3282B] transition-colors" data-testid="back-to-selector">
            <ArrowLeft size={14} />
            {t.back}
          </Link>
          <img src={LOGO} alt="Mother Hippo Foundation" className="h-10 w-auto object-contain" />
          <nav className="hidden md:flex items-center gap-8 font-cormorant italic text-base">
            <a href="#mission" className="hover:text-[#00A651] transition-colors">{t.nav.mission}</a>
            <a href="#programs" className="hover:text-[#00A651] transition-colors">{t.nav.programs}</a>
            <a href="#stories" className="hover:text-[#00A651] transition-colors">{t.nav.stories}</a>
            <a href="#donate" className="px-5 py-2 rounded-full bg-[#E3282B] text-white not-italic font-manrope text-xs tracking-[0.2em] uppercase hover:bg-[#00A651] transition-colors shadow-[0_4px_0_0_#00A651] hover:shadow-[0_4px_0_0_#E3282B]" data-testid="header-donate">{t.nav.donate}</a>
            <LangToggle testId="editorial-lang-toggle" />
          </nav>
          <div className="md:hidden">
            <LangToggle testId="editorial-lang-toggle-mobile" />
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 relative">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-7 mh-rise">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-semibold">
              <span className="h-1 w-10 bg-[#D4A93B]" />
              {t.issue}
            </div>
            <h1
              className="font-cormorant font-medium text-[#1A1A1A] text-5xl sm:text-6xl md:text-7xl lg:text-[128px] leading-[0.92] tracking-tight mt-6"
              data-testid="editorial-hero-title"
            >
              {t.heroA}
              <br />
              <em className="italic text-[#00A651]">{t.heroAccent}</em> {t.heroB}
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-black/75">
              {t.heroSub}
            </p>
            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <a href="#mission" className="px-7 py-3.5 rounded-full bg-[#00A651] text-white text-xs tracking-[0.25em] uppercase hover:bg-[#1A1A1A] transition-colors shadow-[0_6px_0_0_#D4A93B] hover:shadow-[0_6px_0_0_#E3282B]" data-testid="editorial-read-story">
                {t.readStory}
              </a>
              <a href="#donate" className="group inline-flex items-center gap-2 text-sm font-cormorant italic text-[#E3282B]" data-testid="editorial-donate-link">
                <span className="underline underline-offset-4 decoration-[#D4A93B]">{t.becomePatron}</span>
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 mh-rise mh-delay-2">
            <div className="relative">
              <div className="absolute -top-4 -right-4 h-full w-full bg-[#D4A93B] rounded-sm -z-0" />
              <img
                src="https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/k1wg42ea_IMG-20260408-WA0020.jpg"
                alt="STOP Female Genital Mutilation — Mother Hippo Foundation campaign banner"
                className="relative w-full aspect-[4/5] object-contain bg-[#FFF8EC] rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 md:-left-10 bg-[#1A1A1A] text-white px-5 py-4 border-l-4 border-[#00A651] rounded-sm shadow-xl">
                <p className="font-cormorant italic text-lg md:text-xl max-w-[240px] leading-snug">
                  "{t.pullquote}"
                </p>
                <p className="mt-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A93B]">{t.founder}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Meta strip */}
        <div className="mt-24 pt-8 border-t-2 border-[#1A1A1A] grid grid-cols-2 md:grid-cols-4 gap-8">
          {t.stats.map((s, i) => (
            <div key={i}>
              <div
                className="font-cormorant text-5xl md:text-6xl"
                style={{ color: ["#00A651", "#E3282B", "#D4A93B", "#1A1A1A"][i] }}
              >
                {s.k}
              </div>
              <div className="mt-2 text-xs tracking-[0.25em] uppercase text-black/70 font-semibold">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION — drop cap */}
      <section id="mission" className="relative bg-[#00A651] text-white py-24 md:py-32 overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-20 -right-20 h-[400px] w-[400px] rounded-full bg-[#D4A93B]/30 blur-3xl"
        />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 relative">
          <div className="col-span-12 md:col-span-4">
            <div className="sticky top-28">
              <p className="text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">{t.missionKicker}</p>
              <h2 className="font-cormorant text-5xl md:text-6xl leading-[0.95] mt-5">
                {t.missionHeading}
                <br />
                <em className="italic text-[#D4A93B]">{t.missionHeadingItalic}</em> {t.missionHeadingEnd}
              </h2>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <p className="font-cormorant text-2xl md:text-3xl leading-relaxed mh-dropcap">
              {t.missionP1}
            </p>
            <p className="mt-8 text-base leading-relaxed text-white/90">
              {t.missionP2}
            </p>
            <div className="mt-10 flex items-center gap-4 text-sm">
              <div className="h-12 w-12 rounded-full bg-[#D4A93B] border-2 border-white" />
              <div>
                <p className="font-cormorant italic text-lg">{t.founderName}</p>
                <p className="text-xs tracking-[0.2em] uppercase text-white/80">{t.founderTitle}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS — magazine features */}
      <section id="programs" className="py-24 md:py-32 bg-[#FFF8EC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <p className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold">{t.programsKicker}</p>
              <h2 className="font-cormorant text-5xl md:text-7xl leading-[0.95] mt-4">
                {t.programsHeading} <em className="italic text-[#00A651]">{t.programsHeadingAccent}</em>.
              </h2>
            </div>
            <p className="max-w-sm text-sm text-black/70 leading-relaxed">
              {t.programsSub}
            </p>
          </div>
          <div className="space-y-20">
            {t.programs.map((p, i) => (
              <article
                key={i}
                data-testid={`editorial-program-${i + 1}`}
                className={`grid grid-cols-12 gap-8 items-center ${i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""}`}
              >
                <div className="col-span-12 md:col-span-7 relative">
                  <div
                    className="absolute -top-3 -left-3 h-full w-full rounded-sm"
                    style={{ background: ["#00A651", "#E3282B", "#D4A93B"][i] }}
                  />
                  <img
                    src={programImgs[i]}
                    alt={p.title}
                    className="relative w-full aspect-[4/3] object-cover rounded-sm"
                  />
                </div>
                <div className="col-span-12 md:col-span-5">
                  <div
                    className="font-cormorant italic text-4xl"
                    style={{ color: ["#00A651", "#E3282B", "#D4A93B"][i] }}
                  >
                    {p.num}
                  </div>
                  <h3 className="font-cormorant text-4xl md:text-5xl mt-3 leading-[1]">{p.title}</h3>
                  <p className="mt-6 text-lg leading-relaxed text-black/75">{p.excerpt}</p>
                  <a href="#" className="mt-6 inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase hover:text-[#E3282B] transition-colors font-semibold">
                    {t.readFeature} <ArrowUpRight size={14} />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STORIES */}
      <section id="stories" className="bg-[#1A1A1A] text-[#FFF8EC] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">{t.storiesKicker}</p>
          <h2 className="font-cormorant text-5xl md:text-7xl leading-[0.95] mt-4 max-w-3xl">
            {t.storiesHeading} <em className="italic text-[#00A651]">{t.storiesHeadingItalic}</em>.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12">
            {t.stories.map((s, i) => (
              <blockquote
                key={i}
                data-testid={`editorial-story-${i}`}
                className="border-l-4 pl-8"
                style={{ borderColor: i === 0 ? "#00A651" : "#E3282B" }}
              >
                <Quote size={28} className="text-[#D4A93B] mb-6" />
                <p className="font-cormorant italic text-3xl md:text-4xl leading-snug">
                  {s.quote}
                </p>
                <footer className="mt-8 text-sm tracking-[0.2em] uppercase text-[#D4A93B]">
                  {s.name} · {s.place}
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* DONATE */}
      <section id="donate" className="py-24 md:py-32 bg-[#E3282B] text-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
          <p className="text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">{t.donateKicker}</p>
          <h2 className="font-cormorant text-5xl md:text-7xl mt-4 leading-[1]">
            {t.donateHeading} <em className="italic text-[#D4A93B]">{t.donateHeadingAccent}</em>
            <br />{t.donateHeadingEnd}
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-lg text-white/90 leading-relaxed">
            {t.donateSub}
          </p>
          <div className="mt-10 inline-flex items-center gap-3 flex-wrap justify-center">
            {t.tiers.map((tier, i) => (
              <button
                key={i}
                data-testid={`editorial-donate-tier-${i}`}
                className={`px-6 py-3 rounded-full border-2 text-sm tracking-[0.15em] uppercase font-semibold transition-colors ${
                  i === 1
                    ? "bg-[#00A651] text-white border-[#00A651] hover:bg-white hover:text-[#E3282B]"
                    : "border-white hover:bg-white hover:text-[#E3282B]"
                }`}
              >
                {tier}
              </button>
            ))}
          </div>
          <div className="mt-8 text-xs tracking-[0.25em] uppercase text-white/80">
            {t.donateFoot}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-20 md:py-28 bg-[#FFF8EC] border-t-4 border-[#00A651]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <p className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold">{t.contactKicker}</p>
            <h2 className="font-cormorant text-5xl md:text-6xl mt-4 leading-[1]">
              {t.contactHeading} <em className="italic text-[#00A651]">{t.contactHeadingItalic}</em>
            </h2>
            <p className="mt-6 text-black/75 leading-relaxed">
              {t.contactSub}
            </p>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 space-y-5 font-cormorant">
            {[
              { k: t.labels.email, v: t.email, c: "#00A651" },
              { k: t.labels.press, v: t.pressEmail, c: "#E3282B" },
              { k: t.labels.office, v: t.offices, c: "#D4A93B" },
            ].map((c, i) => (
              <div key={i} className="border-b-2 pb-4" style={{ borderColor: c.c }}>
                <div className="text-[11px] tracking-[0.3em] uppercase font-manrope font-bold" style={{ color: c.c }}>{c.k}</div>
                <div className="text-2xl italic mt-1">{c.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-[#FFF8EC] py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <img src={LOGO} alt="" className="h-12 w-auto object-contain" />
          <p className="font-cormorant italic text-lg max-w-md text-[#D4A93B]">
            {t.footerTag}
          </p>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">
            {t.rights}
          </div>
        </div>
      </footer>
    </div>
  );
}
